import type { IntlShape } from "react-intl";

// Maps the active route to the agent-service `section`, which gates the
// tools the Assistant may use. The agent-service expects
// `save/profile | save/community | growth | share | match`; our route segment
// for growth is `grow`. Within Save, only `/save/contact` is the contacts
// (Community) surface — every other Save route (and the bare landing) is
// Profile (own profile + CV editing). Pages without a matching section (home,
// search, settings, admin, person profile) fall back to `save/profile` — the
// Assistant still works with base tools.
export function sectionForPath(pathname: string): string {
  const segs = pathname.replace(/^\//, "").split("/");
  const seg = segs[0];
  if (seg === "grow") return "growth";
  if (seg === "match" || seg === "share") return seg;
  if (seg === "save") return segs[1] === "contact" ? "save/community" : "save/profile";
  return "save/profile";
}

/**
 * Values used for server-side tool binding. Keep these separate from the
 * natural-language UI context so the model cannot choose or alter the active
 * match ID when it calls a parametrized match tool.
 */
export function agentBindingContextForPath(
  pathname: string,
): { matchId: string } | undefined {
  const match = pathname.match(/^\/match\/([^/]+)\/?$/);
  if (!match?.[1]) return undefined;
  try {
    return { matchId: decodeURIComponent(match[1]) };
  } catch {
    return undefined;
  }
}

// Static per-section starter prompts shown in an empty Assistant chat (the agent only returns
// follow-ups after a reply, so the first stage needs local copy). Ten per section; keys live in
// i18n/messages/{en,de}.json under `agent.starters.<section>.{0..9}`. The caller samples a few.
// Starter pools are keyed by the TOP-LEVEL section, so a subsectioned value (`save/profile`) reuses
// the `save` pool.
export function starterPrompts(intl: IntlShape, section: string): string[] {
  const base = section.split("/")[0];
  return Array.from({ length: 10 }, (_, i) =>
    intl.formatMessage({ id: `agent.starters.${base}.${i}` }),
  );
}
